import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { films } from "@/mocks/films";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilmGrid from "@/components/feature/FilmGrid";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getRelatedFilms, typeLabel } from "@/utils/film";

export default function Detail() {
  const { id = "" } = useParams();
  const { favorites, toggleFavorite } = useFavorites();

  const film = useMemo(() => films.find((f) => f.id === id), [id]);

  const related = useMemo(
    () => (film ? getRelatedFilms(film, films, 4) : []),
    [film],
  );

  usePageMeta(
    film ? `${film.title} - 伦理片在线观看` : "作品不存在 - 伦理片在线观看",
    film
      ? `${film.title}，${film.director}导演、${film.cast.join("、")}主演等信息介绍，免费在线观看伦理影视作品。`
      : "抱歉，您访问的伦理影视作品不存在，请返回首页浏览更多内容。",
  );

  if (!film) {
    return (
      <div className="mx-auto flex max-w-6xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-secondary-500">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">作品不存在</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          抱歉，您访问的影视作品可能已被下架或链接有误。
        </p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = favorites.includes(film.id);
  const metaItems = [
    { label: "导演", value: film.director },
    { label: "主演", value: film.cast.join(" / ") },
    { label: "年份", value: String(film.year) },
    { label: "类型", value: typeLabel(film.type) },
    { label: "地区", value: film.region },
    { label: "上映时间", value: film.releaseDate },
    { label: "片长", value: film.runtime },
    { label: "题材", value: film.genres.join(" · ") },
  ];

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: typeLabel(film.type), to: `/category/${film.type}` },
              { label: film.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-8 lg:flex-row">
            <div className="w-full max-w-[280px] shrink-0">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <img
                  src={film.poster}
                  alt={`${film.title} 海报`}
                  title={`${film.title} - 伦理影视作品`}
                  className="h-[380px] w-full object-cover object-top"
                />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex flex-1 flex-col items-center rounded-md bg-background-50 py-2.5">
                  <span className="flex items-center gap-1 font-heading text-xl font-bold text-accent-600">
                    <i className="ri-star-fill text-sm"></i>
                    {film.rating.toFixed(1)}
                  </span>
                  <span className="mt-0.5 text-xs text-secondary-500">综合评分</span>
                </div>
                <div className="flex flex-1 flex-col items-center rounded-md bg-background-50 py-2.5">
                  <span className="flex items-center gap-1 font-heading text-xl font-bold text-primary-700">
                    <i className="ri-fire-line text-sm"></i>
                    {film.popularity}
                  </span>
                  <span className="mt-0.5 text-xs text-secondary-500">热度指数</span>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                <i className="ri-bookmark-line"></i>
                {typeLabel(film.type)}
              </span>
              <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                {film.title}
              </h1>
              <div className="mt-3 flex flex-wrap gap-2">
                {film.genres.map((g) => (
                  <span
                    key={g}
                    className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex gap-3 text-sm">
                    <dt className="shrink-0 text-secondary-500">{item.label}</dt>
                    <dd className="text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(film.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "border border-primary-300 text-primary-700 hover:bg-primary-50"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "加入收藏"}
                </button>
                <Link
                  to={`/category/${film.type}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-apps-2-line"></i>
                  查看更多{typeLabel(film.type)}
                </Link>
              </div>
              <p className="mt-4 flex items-center gap-1.5 text-xs text-secondary-400">
                <i className="ri-information-line"></i>
                本站仅提供影视资料展示，暂不提供在线播放服务。
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
        <section>
          <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-600">
              <i className="ri-file-text-line text-lg"></i>
            </span>
            剧情简介
          </h2>
          <p className="mt-4 whitespace-pre-line text-sm leading-7 text-foreground-700">
            {film.synopsis}
          </p>
        </section>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                <i className="ri-movie-2-line text-lg"></i>
              </span>
              相关推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              根据同导演、同类型及相同题材为你推荐
            </p>
            <div className="mt-6">
              <FilmGrid
                films={related}
                favorites={favorites}
                onToggleFavorite={toggleFavorite}
                columns={4}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}